/* eslint-disable max-lines */
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getKioskAuthorization, kioskPaymentStop } from 'api/Kiosk';

import useError from 'lib/hooks/useError';
import { cn, inRn, snakeToTitleCase } from 'lib/helpers/utility';
import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';
import { PAID_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';
import {
  CARD_ON_FILE_REQUIRED_MESSAGE,
  LOW_ASSURANCE_RESPONSE_CODES
} from 'lib/constants/healthcareProgram/fields';

import Modal from 'components/shared/Modal/Modal';
import Loader from 'components/shared/Loader/Loader';
import { showNotification } from 'components/shared/Notification/Notification';
import { useMarkKioskSaleArmed } from 'components/Kiosk/lib/useKioskSaleArmed';
import ErrorPaymentModal from 'components/Payments/NewPayment/PaymentContent/components/ErrorModal';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import { PAYMENT_MODAL_SIZE } from '../PaymentEntryModal/lib/constants';
import { FormLoadErrorContext } from '../PaymentEntryModal/lib/formLoadErrorContext';

import Footer from './components/Footer';
import Header from './components/Header';
import CardContent from './components/CardContent';
import Information from './components/Information';
import { classifyReaderStatus } from './lib/readerStatus';
import { toDisplayPaymentMessage } from './lib/paymentMessages';

const RECONCILE_AFTER_MS = 60000;
// A payment pushed from the portal arms the reader before the patient has reached the kiosk, so
// the walk-over has to fit inside the window that cancels the sale.
const AUTO_STARTED_RECONCILE_AFTER_MS = 120000;
// Reader phases in which a sale is still with the terminal and its outcome is pending.
const LIVE_SALE_STATUSES = ['initiating', 'ready', 'authorizing'];

const PayFromKioskModal = ({
  isOpen,
  handleClose,
  patientId,
  invoice,
  onSuccess,
  token,
  autoStarted = false,
  saveCardOnly = false
}) => {
  const [paymentKind, setPaymentKind] = useState('saved-cards');
  const [formLoadError, setFormLoadError] = useState(null);
  const [cardMessage, setCardMessage] = useState('');
  // Reader phase: initiating | ready | authorizing | success | canceled | error.
  const [status, setStatus] = useState('initiating');
  const [isClosing, setIsClosing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [transactionId, setTransactionId] = useState(null);

  const isSuccess = status === 'success';

  const { values, terminalData, processor, practiceId } = usePaymentContext();

  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();
  const navigate = useNavigate();

  const timeoutRef = useRef(null);
  const retryingRef = useRef(false);

  // The tip rides on top of the due amount: `useTipPrompt` folds it into `values.amount`, but the
  // server adds `gratuity` itself, so the balance it is sent excludes it. The server caps the tip
  // at the amount due (surcharge included), so the total shown here is capped the same way.
  const tipEntered = saveCardOnly ? 0 : Math.max(Math.round(inRn(values?.gratuity)), 0);
  const balanceBeforeTip = Math.max(inRn(values?.amount) - tipEntered, 0);
  const gratuity = Math.min(tipEntered, balanceBeforeTip);
  const chargedAmount = balanceBeforeTip + gratuity;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['kiosk-authorization', patientId, saveCardOnly, gratuity, !!values?.saveCard],
    queryFn: () =>
      getKioskAuthorization(
        navigate,
        saveCardOnly
          ? {
              patientId,
              practiceId,
              saveCard: true,
              saveCardOnly: true,
              processor: terminalData?.cardPresentTerminal?.processor
            }
          : {
              patientId,
              balance: balanceBeforeTip,
              gratuity,
              saveCard: !!values?.saveCard,
              token: values?.invoiceToken || token,
              processor: terminalData?.cardPresentTerminal?.processor
            }
      ),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0
  });

  // Keep the latest onSuccess without re-creating the socket handler each render.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // What `onSuccess` receives once a stop call reports the job settled: the charge, or the stored card.
  const settledId = useCallback(
    (stopData) => (saveCardOnly ? (stopData?.cardId ?? null) : stopData?.transaction?.id),
    [saveCardOnly]
  );

  // A save-card session raises no sale, so the stop call settles it by naming the stored card
  // (`cardId`) rather than a paid transaction.
  const isSettled = useCallback(
    (stopData) =>
      saveCardOnly
        ? !!stopData?.cardId
        : PAID_TRANSACTION_STATUSES.includes(stopData?.transaction?.status),
    [saveCardOnly]
  );

  const switchToManualEntry = useCallback(() => {
    if (saveCardOnly) setStatus('error');
    else setPaymentKind('credit-card');
  }, [saveCardOnly]);

  const clearTimeoutHandler = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // The checkin page renders the success screen itself, so the modal hands the
  // transaction off and closes as soon as the charge settles.
  const finishSuccess = useCallback(
    (paidTransactionId) => {
      clearTimeoutHandler();
      setCardMessage('');
      setTransactionId(paidTransactionId ?? null);
      setStatus('success');
      onSuccessRef.current(paidTransactionId ?? null);
      handleClose();
    },
    [clearTimeoutHandler, handleClose]
  );

  const armReconcileTimeout = useCallback(() => {
    clearTimeoutHandler();

    // Safety net: if the live success/error push never lands, reconcile against
    // the actual transaction so a completed charge still resolves the modal
    // instead of hanging on the last card message.
    timeoutRef.current = setTimeout(
      async () => {
        const stopData = await kioskPaymentStop(navigate, {});
        if (isSettled(stopData)) {
          finishSuccess(settledId(stopData));
        } else if (stopData?.transaction?.status) {
          if (stopData?.message?.includes('TransactionCardReaderNoneAvailable')) {
            switchToManualEntry();
          } else {
            setStatus('error');
            setErrorMessage(
              toDisplayPaymentMessage(
                stopData?.message,
                "We couldn't finish this payment. Please ask us for help."
              )
            );
          }
        } else {
          setStatus('error');
          setCardMessage('Terminal not connected');
          showNotification({
            icon: 'new-info',
            iconColor: 'warning',
            title: 'Terminal not connected'
          });
        }
      },
      autoStarted ? AUTO_STARTED_RECONCILE_AFTER_MS : RECONCILE_AFTER_MS
    );
  }, [
    autoStarted,
    clearTimeoutHandler,
    finishSuccess,
    isSettled,
    navigate,
    setErrorMessage,
    settledId,
    switchToManualEntry
  ]);

  const resetAndClose = () => {
    handleClose();
    clearTimeoutHandler();
  };

  const closeModal = async () => {
    if (isClosing) return;

    // Already charged — closing is the hand-off, so there's no terminal sale to stop.
    if (status === 'success') {
      onSuccessRef.current(transactionId);
      resetAndClose();
      return;
    }

    setIsClosing(true);

    // Cancelling here (Cancel button, X, or backdrop click) must tell the kiosk
    // app to cancel the in-flight terminal transaction — otherwise the reader is
    // left mid-charge. Reuse the same stop call as the safety-net timeout and
    // reconcile: if the charge actually completed in the race window, resolve as
    // success instead of dropping a real payment.
    try {
      const stopData = await kioskPaymentStop(navigate, {});
      if (isSettled(stopData)) {
        finishSuccess(settledId(stopData));
        return;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsClosing(false);
    }

    resetAndClose();
  };

  const handleRetry = async () => {
    // State can't gate concurrent taps — the flag has to flip in the same tick as the check.
    if (retryingRef.current) return;
    retryingRef.current = true;

    clearTimeoutHandler();
    setIsRetrying(true);
    setCardMessage('');
    setErrorMessage(null);
    setStatus('initiating');

    try {
      // The reader may have approved the sale before the error surfaced; re-arming without
      // reconciling first charges the patient twice.
      const stopData = await kioskPaymentStop(navigate, {});
      if (isSettled(stopData)) {
        finishSuccess(settledId(stopData));
        return;
      }

      // Manual card entry has no terminal sale to re-arm — retrying only clears the error.
      if (paymentKind === 'credit-card') return;

      const { data: retried } = await refetch();

      if (!retried || retried.code !== 0) {
        setStatus('error');
        return;
      }

      if (retried.readerStatus === 'unavailable') {
        switchToManualEntry();
        return;
      }

      armReconcileTimeout();
    } catch (e) {
      console.error(e);
      setStatus('error');
    } finally {
      retryingRef.current = false;
      setIsRetrying(false);
    }
  };

  const goBack = () =>
    paymentKind === 'credit-card' ? setPaymentKind('saved-cards') : closeModal();

  const handleKioskMessage = useCallback(
    (msgData) => {
      switch (msgData?.kind) {
        case 'handlePaymentConfirmationReceived':
          // The reader only accepted the sale job — no card prompt yet, so the phase is
          // unchanged and the reconciliation timeout stays armed (`success` can be missed).
          break;
        case 'switchToAlternative':
          switchToManualEntry();
          break;
        case 'success':
          // A stored card raises no sale, so it reports the card rather than a transaction.
          finishSuccess(saveCardOnly ? msgData?.cardId : msgData?.transactionId);
          break;
        case 'readerWaking':
          // The reader has not taken the sale yet, so the phase stays `initiating` — only the
          // caption changes, and the reconciliation timeout stays armed.
          if (typeof msgData?.message === 'string') setCardMessage(msgData.message);
          break;
        case 'handleCardMessageUpdate':
          setCardMessage(snakeToTitleCase(msgData?.message.toLowerCase()));
          setStatus((prev) => {
            const next = classifyReaderStatus(msgData?.message);
            // An approved payment can never be downgraded, and an AUTHORISING prompt that
            // lands before any card prompt is reader setup noise, not real authorization.
            if (prev === 'success') return prev;
            if (prev === 'initiating' && next === 'authorizing') return prev;
            return next;
          });
          break;
        case 'canceled':
          clearTimeoutHandler();
          setStatus('canceled');
          setCardMessage('');
          break;
        case 'error':
          if (msgData?.code === 0) {
            switchToManualEntry();
          } else if (msgData?.invoiceId) {
            // No invoice means no sale to fail — idle readers raise one per terminal-info ping.
            const errorMessage = toDisplayPaymentMessage(
              msgData?.message,
              'Terminal not connected'
            );
            setStatus('error');
            setCardMessage(errorMessage);
            showNotification({
              icon: 'new-info',
              iconColor: 'warning',
              title: errorMessage
            });
          }
          break;
        default:
          if (saveCardOnly && msgData?.cardId) finishSuccess(msgData.cardId);
          else if (msgData?.transactionId && !msgData?.message)
            finishSuccess(msgData?.transactionId);
          break;
      }
    },
    [finishSuccess, clearTimeoutHandler, saveCardOnly, switchToManualEntry]
  );

  // A non-zero code means authorization failed server-side and no sale was ever handed to
  // the reader; arming the socket + safety net anyway strands the modal until the timeout
  // reconciles against an unrelated transaction and reports a payment that never happened.
  const authFailed = !!data && data.code !== 0;

  // The reader is down or the practice has no terminal, so authorization never handed a sale to a
  // device: waiting on a card prompt that can't arrive only burns the window before erroring.
  const readerUnavailable = !isFetching && data?.readerStatus === 'unavailable';

  // Manual card entry is typed on the glass, so only the reader path needs the idle timer paused.
  useMarkKioskSaleArmed(
    isOpen &&
      paymentKind === 'saved-cards' &&
      !authFailed &&
      !readerUnavailable &&
      !!data?.kioskAuthorization &&
      data.kioskAuthorization !== 'not_configured' &&
      LIVE_SALE_STATUSES.includes(status)
  );

  // Waits for the fetch to settle so a cached failure from a previous open can't race the
  // open reset, and a retry that still fails re-applies the message.
  useEffect(() => {
    if (!isOpen || isFetching || !authFailed) return;
    if (data.code === LOW_ASSURANCE_RESPONSE_CODES.CARD_ON_FILE_REQUIRED) {
      setCardMessage(CARD_ON_FILE_REQUIRED_MESSAGE);
    }
    setStatus('error');
  }, [isOpen, isFetching, authFailed, data?.code]);

  useEffect(() => {
    const kAuth = data?.kioskAuthorization;

    try {
      if (!isOpen || authFailed || readerUnavailable || !kAuth || kAuth === 'not_configured')
        return undefined;

      socketOn('kioskPaymentScreenMessage', handleKioskMessage);
      socketEmit('kioskJoinClient', { kioskAuthorization: kAuth }, { reemitOnReconnect: true });

      armReconcileTimeout();
    } catch (e) {
      console.error(e);
    }

    return () => {
      socketOff('kioskPaymentScreenMessage', handleKioskMessage);
      clearTimeoutHandler();
    };
  }, [
    isOpen,
    authFailed,
    readerUnavailable,
    data?.kioskAuthorization,
    handleKioskMessage,
    armReconcileTimeout,
    clearTimeoutHandler
  ]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket.connected]);

  useEffect(() => {
    if (isOpen) {
      setStatus('initiating');
      setCardMessage('');
      setPaymentKind('saved-cards');
      setTransactionId(null);
      setErrorMessage(null);
      setIsClosing(false);
      setIsRetrying(false);
      retryingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && readerUnavailable) switchToManualEntry();
  }, [isOpen, readerUnavailable, switchToManualEntry]);

  const isTapStep = !isSuccess && paymentKind === 'saved-cards';
  const hasFooterAction =
    status === 'error' || status === 'canceled' || (!!values.methodDetail && !saveCardOnly);

  const hideCardOptions =
    status === 'success' || (paymentKind === 'saved-cards' && status === 'authorizing');

  return (
    <FormLoadErrorContext.Provider value={setFormLoadError}>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        isCentered={!isSuccess}
        modalCenterAnimation={!isSuccess}
        width="full"
        bodyClassName="p-0"
        customTitle={
          <Header
            goBack={goBack}
            step={paymentKind}
            isDisabled={isClosing}
            title={saveCardOnly ? 'Use another card' : undefined}
          />
        }
        isFooter={!isTapStep || hasFooterAction}
        footer={
          isTapStep && !hasFooterAction ? null : (
            <Footer
              step={paymentKind}
              amount={values.amount}
              status={status}
              onClose={isSuccess ? closeModal : goBack}
              onRetry={handleRetry}
              isClosing={isClosing}
              isRetrying={isRetrying}
              saveCardOnly={saveCardOnly}
              onRetryFormLoad={formLoadError?.retry}
            />
          )
        }
        overlayClassName="xs:!p-0"
        className={cn(
          'xs:!rounded-none',
          !isSuccess && PAYMENT_MODAL_SIZE,
          isTapStep && '!h-[min(683px,calc(var(--app-height,100vh)_-_2rem))]'
        )}>
        <div className="flex h-full min-h-full flex-col">
          <div
            key={paymentKind}
            className={cn(
              !isSuccess
                ? cn(
                    'flex w-full animate-enter-up flex-col items-center',
                    isTapStep ? 'px-5 pt-5' : 'gap-4 px-6 py-4 xs:px-4'
                  )
                : 'flex h-full w-full flex-1 flex-col'
            )}
            ref={scrollRef}>
            <Information
              paymentKind={paymentKind}
              cardMessage={cardMessage}
              amount={chargedAmount}
              gratuity={gratuity}
              surchargeAmount={values?.surchargeAmount}
              costShare={invoice?.last_applied}
              status={status}
              showManualEntry={
                !saveCardOnly && (processor === 'payroc' || !!terminalData?.onlineTerminal)
              }
              transactionId={transactionId}
              token={values?.invoiceToken || token}
              saveCardOnly={saveCardOnly}
              formLoadError={paymentKind === 'credit-card' ? formLoadError : null}
            />
            {isLoading ? (
              <Loader color="primary" size={56} />
            ) : hideCardOptions || saveCardOnly ? null : (
              <div className={cn('flex w-full justify-center', isTapStep && 'px-10 pb-8 pt-6')}>
                <CardContent
                  ref={scrollRef}
                  active={paymentKind}
                  setActive={setPaymentKind}
                  setErrorMessage={setErrorMessage}
                  patientId={patientId}
                />
              </div>
            )}
          </div>
        </div>
        <ErrorPaymentModal
          isOpen={!!error}
          message={error}
          onClearError={() => setErrorMessage(null)}
          onClose={closeModal}
          onTryAgain={handleRetry}
        />
      </Modal>
    </FormLoadErrorContext.Provider>
  );
};

export default PayFromKioskModal;
